import React, {
  FormEvent,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { navigate } from 'gatsby';
import { useLocation } from '@gatsbyjs/reach-router';
import { useSelector } from 'react-redux';
import { AutocompleteCollection, AutocompleteState } from '@algolia/autocomplete-core';
import {
  AutocompleteQuerySuggestionsHit,
} from '@algolia/autocomplete-plugin-query-suggestions/dist/esm/types';
import { addDataLayerEntry, addEventEntry } from '@printdeal/data-layer-manager';
import { debounce } from 'es-toolkit';
import { useIntl } from 'react-intl';
import { useExperiment } from '@printdeal/feature-management/react';
import { Transition, scrollTo, useMediaQuery } from '@printdeal/ui-library';
// @ts-expect-error - TODO: types for customerSlice and authSlice
import { selectors as customerSelectors } from '../../../../store/slices/customerSlice';
import { selectors as authSelectors } from '../../../../store/slices/authSlice';
import AlgoliaSearch from '../../../algolia-search/AlgoliaSearch';
import algoliaClient from '../../../algolia-search/algoliaClient';
import { filterSearchResponses, INDICES } from '../../../algolia-search/helper/algoliaHelper';
import { getScrollBarWidth } from '../../../../helpers/window';
import useSystemPages from '../../../../hooks/useSystemPages';
import { searchResultsMessages, searchBarMessages } from './SearchMessages';
import { hotjarEvent } from '../../../../helpers/HotjarHelper';
import {
  dispatchDataLayerEventOnSearchQueryGA4,
  dispatchDataLayerEventOnSearchResultsClickGA4,
  dispatchDataLayerEventOnShowAllSearchResultsClickGA,
} from './helpers/SearchDataLayerHelper';
import { createAutocompleteApi } from './helpers/autocompleteHelper';
import {
  dispatchDataLayerEventOnRecentSearchLoad,
  dispatchDataLayerEventOnRecentSearchSelect,
  dispatchDataLayerEventOnSuggestionLoad,
  dispatchDataLayerEventOnSuggestionSelect,
  dispatchDataLayerEventOnNoResultsRequestQuoteGA,
} from './helpers/SearchSuggestionDataLayerHelper';
import { HeaderContext } from '../context';
import {
  addQueryToRecentSearches,
  hasRecentSearches,
} from './helpers/recentSearchesHelpers';
import { useClickOutside } from '../../../../hooks/useClickOutside';
import { SearchResponsePerIndex } from '../../../algolia-search/types';
import { getIndicesForLocale } from './helpers/getIndicesForLocale';
import { getPlaceHolder, IPlaceHolder } from './helpers/placeholderHelpers';
import { dispatchDataLayerPlaceHolderExperiment } from './helpers/placeholderDataLayerHelper';
import SearchSuggestions, { AutocompleteApiWithSuggestions } from './SearchSuggestions';
import SearchResultsDropdown from './SearchResultsDropdown';
import { getConfiguration } from '../../../../helpers/Configuration';
import { useUserTypeAlgoliaClick } from '../../../../hooks/search/useUserTypeAlgoliaClick';

const EVENT_ENTRY_BUFFER_TIME = 2000;
const DATA_LAYER_EVENT_CATEGORY = 'site_search_algolia';
const DATA_LAYER_EVENT_NAME = 'search_site';
const DATA_LAYER_EVENT_CATEGORY_STICKY = 'site_search_algolia_sticky';
const DATA_LAYER_EVENT_NAME_STICKY = 'search_site_sticky';

const LIVE_SEARCH_INDEX_CONFIG = [
  {
    name: INDICES.PDP_INDEX,
    hitsPerPage: 4,
  }, {
    name: INDICES.PROD_CAT_INDEX,
    hitsPerPage: 2,
  }, {
    name: INDICES.CONTENT_PAGES_INDEX,
    hitsPerPage: 2,
  },
];

const getAlgoliaNbHits = (result: SearchResponsePerIndex[] = []) => result.reduce((acc, searchResp) => {
  const addition = searchResp.searchResults?.nbHits || 0;
  return acc + addition;
}, 0);

interface SearchProps {
  id?: string;
  className?: string;
  disableScrollOnOpen?: boolean;
  scrollIntoView?: boolean;
  scrollIntoViewSpacing?: number;
  staticHeaderInView?: boolean;
}

type SuggestionState = AutocompleteState<AutocompleteQuerySuggestionsHit>;
export type CollectionState = AutocompleteCollection<AutocompleteQuerySuggestionsHit>[];
const { staticPlaceholderForAlgoliaSearch } = getConfiguration();

const Search = ({
  id,
  className,
  disableScrollOnOpen = true,
  scrollIntoView = false,
  scrollIntoViewSpacing = 20,
  staticHeaderInView,
}: SearchProps) => {
  const intl = useIntl();
  const { locale } = intl;
  const location = useLocation();

  const {
    setIsSearchOpen,
    isClearSearch,
  } = useContext(HeaderContext);

  const { isAllCategoriesNavigationOpen } = useContext(HeaderContext);

  const searchSuggestionIndex = getIndicesForLocale(locale);

  const [searchTerm, setSearchTerm] = useState('');
  const [scrollBarWidth, setScrollbarWidth] = useState(0);
  const [searchResultsPage] = useSystemPages('searchResults');

  const [searchResponses, setSearchResponses] = useState<SearchResponsePerIndex[]>([]);
  const [totalSearchResults, setTotalSearchResults] = useState(0);
  const [countAlgoliaQuery, setCountAlgoliaQuery] = useState(0);
  const [clearSearch, setClearSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isDatalayerEventSet, setIsDatalayerEventSet] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [searchValue, setSearchValue] = useState<string>('');

  const [autocompleteCollections, setAutocompleteCollections] = useState<CollectionState>([]);
  const [autocompleteOpen, setAutocompleteOpen] = useState<boolean>(false);
  const [placeholderText, setPlaceholderText] = useState<string>('');
  // removed the manual even fire on useEffect, overriding the is oninitilized to get custom ga eventAction
  const { experiment: placeholderExperiment, isInitialized: isPlaceholderExperimentInitialized } = useExperiment<IPlaceHolder>('search4it_algolia_placeholder_text', ({ experiment }) => {
    const { experimentCode, variation } = experiment.variables;
    if (variation) dispatchDataLayerPlaceHolderExperiment(experimentCode, variation);
  });
  const isLiveSearchOpen = searchResponses && isOpen;
  const isLoggedIn = useSelector(authSelectors?.isLoggedIn);
  const firstName = useSelector(customerSelectors?.getCustomerFirstName) as string || '';
  const suggestionRef = useRef<HTMLDivElement>(null);

  const placeholderLoadingText = intl.formatMessage(searchBarMessages.placeholderLoading);
  const placeholderIntl = intl.formatMessage(searchBarMessages.placeholder);
  // Hook to send Algolia click events based on the user's type (Enduser, Reseller, or Default)
  const userTypeAlgoliaClickEvent = useUserTypeAlgoliaClick();

  useEffect(() => {
    let placeholder: string = '';
    if (staticPlaceholderForAlgoliaSearch.enabled) {
      placeholder = placeholderIntl;
    } else {
      if (!isPlaceholderExperimentInitialized) return;
      placeholder = getPlaceHolder(
        {
          ...placeholderExperiment.variables,
          firstName,
          isLoggedIn,
          locale,
          placeholderIntl,
        },
      );
    }
    setPlaceholderText(placeholder);
  }, [isPlaceholderExperimentInitialized, isLoggedIn, staticPlaceholderForAlgoliaSearch.enabled]);

  useEffect(() => {
    if (!autocompleteOpen || isOpen) return;
    dispatchDataLayerEventOnSuggestionLoad(autocompleteCollections, staticHeaderInView);

    if (hasRecentSearches()) {
      dispatchDataLayerEventOnRecentSearchLoad(autocompleteCollections);
    }
  }, [autocompleteOpen]);

  const closeAutoSuggest = () => {
    setAutocompleteOpen(false);
  };

  useEffect(() => {
    if (!isAllCategoriesNavigationOpen) return;
    closeAutoSuggest();
  }, [isAllCategoriesNavigationOpen]);

  const handleAutoCompleteStateChange = (state: SuggestionState): void => {
    setAutocompleteCollections(state.collections);
    setAutocompleteOpen(state.isOpen);
  };

  const handleAutocompleteSelect = (value: string, sourceId: string) => {
    setSearchValue(value || '');

    if (!value.length) return;
    if (sourceId === 'RecentSearches') {
      dispatchDataLayerEventOnRecentSearchSelect(value);
      return;
    }

    dispatchDataLayerEventOnSuggestionSelect(value, staticHeaderInView);
  };

  const autocomplete = useMemo(() => createAutocompleteApi({
    searchSuggestionIndex,
    onStateChange: handleAutoCompleteStateChange,
    onSelect: handleAutocompleteSelect,
  }), []);
  const searchResultUrl = `${searchResultsPage?.path}?q=${encodeURIComponent(searchTerm)}`;

  const isMobileBreakpoint = useMediaQuery('(max-width: 991px)');
  const searchWrapperRef = useRef<HTMLDivElement | null>(null);

  const onSearchValueSubmit = (event: FormEvent<HTMLFormElement>, skipDataLayerEvent: boolean) => {
    event.preventDefault();
    navigate(`${searchResultsPage?.path}?q=${encodeURIComponent(searchTerm)}`,
      { state: skipDataLayerEvent !== undefined ? { skipDataLayerEvent } : undefined });
  };

  const searchQueryRef = useRef('');
  const isDatalayerEventSetRef = useRef(false);
  searchQueryRef.current = searchQuery;
  isDatalayerEventSetRef.current = isDatalayerEventSet;

  const handleEventEntry = useCallback(debounce((
    eventCategory: string,
    eventAction: string,
    searchResults: SearchResponsePerIndex[],
  ) => {
    if (isDatalayerEventSetRef.current) return;

    const nbHits = getAlgoliaNbHits(searchResults);
    const eventLabel = JSON.stringify({ results: nbHits.toString(), query: searchQueryRef.current });
    const eventObject = {
      eventCategory, eventAction, eventLabel, eventNonInteraction: true,
    };
    addDataLayerEntry(eventAction, eventObject);
    addEventEntry(eventObject);
    setIsDatalayerEventSet(true);
  }, EVENT_ENTRY_BUFFER_TIME), []);

  // Live Search
  const handleSearchPopupOpen = () => {
    setIsSearchOpen(true);
    setIsOpen(true);

    if (!disableScrollOnOpen) return;

    document.body.style.overflow = 'hidden';
    if (!isMobileBreakpoint) document.body.style.paddingRight = `${scrollBarWidth}px`;
  };

  const scrollToPosition = (targetTop: number) => {
    scrollTo(document.documentElement, {
      top: targetTop,
      behavior: 'smooth',
    });
  };

  const scrollToInput = () => {
    if (scrollIntoView === false) {
      return;
    }
    const searchWrapperTop = (searchWrapperRef.current?.getBoundingClientRect()?.top || 0)
      + document.documentElement.scrollTop;
    scrollToPosition(searchWrapperTop - scrollIntoViewSpacing);
  };

  const scrollToTop = () => {
    scrollToPosition(0);
  };

  const closeSearch = () => {
    scrollToTop();
    setIsSearchOpen(false);
    setIsOpen(false);
    setClearSearch(true);
    setCountAlgoliaQuery(0);

    if (!disableScrollOnOpen) return;

    document.body.style.overflowY = 'auto';
    if (!isMobileBreakpoint) document.body.style.paddingRight = '';
  };

  useClickOutside(suggestionRef, closeAutoSuggest);

  const handleOnFocus = useCallback<React.FocusEventHandler<HTMLInputElement>>((e) => {
    setAutocompleteOpen(true);
    autocomplete.getInputProps({ inputElement: null }).onFocus(e as unknown as Event);
  }, [autocomplete]);

  const handleClear = () => {
    setClearSearch(true);
    setSearchResponses([]);
    setTotalSearchResults(0);
    setCountAlgoliaQuery(0);
    setSearchQuery('');
    setSearchValue('');
    closeSearch();
  };

  const onSearchQueryChange = useCallback((query: string) => {
    if (formSubmitted) {
      setFormSubmitted(false);
    }
    if (query) {
      scrollToInput();
      setCountAlgoliaQuery(countAlgoliaQuery + 1);
      setSearchQuery(query);
      setSearchTerm(query);
      closeAutoSuggest();
    } else {
      scrollToTop();
      handleClear();
    }
  }, [formSubmitted, countAlgoliaQuery]);

  const getSearchResults = useCallback((response: SearchResponsePerIndex[], searchText: string) => {
    if (clearSearch) {
      setClearSearch(false);
      return;
    }

    const total = getAlgoliaNbHits(response);
    const testIdResponse = response.find((result) => result?.searchResults?.abTestID);
    const algoliaABTestIds = {
      abTestID: testIdResponse?.searchResults?.abTestID,
      abTestVariantID: testIdResponse?.searchResults?.abTestVariantID,
    };

    if (searchQuery) {
      setIsDatalayerEventSet(false);
      handleEventEntry(staticHeaderInView === false ? DATA_LAYER_EVENT_CATEGORY_STICKY : DATA_LAYER_EVENT_CATEGORY,
        staticHeaderInView === false ? DATA_LAYER_EVENT_NAME_STICKY : DATA_LAYER_EVENT_NAME,
        response);
      if (searchText) {
        addQueryToRecentSearches(searchText);
        dispatchDataLayerEventOnSearchQueryGA4('search_query', searchQuery, total, algoliaABTestIds);
      }
    }

    if (response.length && !formSubmitted) {
      handleSearchPopupOpen();
    }
    setSearchResponses(response);
    setTotalSearchResults(total);
  }, [clearSearch, searchQuery, handleEventEntry, staticHeaderInView, formSubmitted]);

  const submitHandler = (e: FormEvent<HTMLFormElement>) => {
    handleEventEntry(staticHeaderInView === false ? DATA_LAYER_EVENT_CATEGORY_STICKY : DATA_LAYER_EVENT_CATEGORY,
      staticHeaderInView === false ? DATA_LAYER_EVENT_NAME_STICKY : DATA_LAYER_EVENT_NAME,
      searchResponses);
    setFormSubmitted(true);
    closeSearch();
    onSearchValueSubmit(e, true);
  };

  // get the scrollBar width on DOM loaded once
  useEffect(() => {
    setScrollbarWidth(getScrollBarWidth());
  }, []);

  useEffect(() => {
    handleClear();
  }, [isClearSearch]);

  // Close the search when the url changes
  useEffect(() => {
    closeSearch();
  }, [location.href]);

  // Trigger Hotjar event when 1st char. is typed on AlgoliaSearch
  useEffect(() => {
    if (countAlgoliaQuery === 1) {
      hotjarEvent('AlgoliaSearch');
    }
  }, [countAlgoliaQuery]);

  return (
    <form onSubmit={submitHandler} className={className} role="search">
      <div id="AlgoliaSearch" ref={searchWrapperRef}>
        <div className="pd-relative">
          <AlgoliaSearch
            id={id}
            searchClient={algoliaClient()}
            onSearchResponseSuccess={getSearchResults}
            indices={LIVE_SEARCH_INDEX_CONFIG}
            className="pd-w-full"
            placeholder={placeholderText}
            placeholderLoading={placeholderLoadingText}
            clearSearch={clearSearch}
            onSearchQueryChange={onSearchQueryChange}
            onSearchValueChange={setSearchTerm}
            onFocus={handleOnFocus}
            onBlur={() => setAutocompleteOpen(false)}
            locale={locale}
            autoComplete="off"
            value={searchValue}
            data-testid="algolia-search-input"
            allowHotjar
          />
          <Transition
            as={Fragment}
            show={autocompleteOpen && !searchQuery.length}
            variant="appear-from-top"
          >
            <SearchSuggestions
              collections={autocompleteCollections}
              autocompleteApi={autocomplete as AutocompleteApiWithSuggestions}
              ref={suggestionRef}
            />
          </Transition>
        </div>

        <SearchResultsDropdown
          searchResults={filterSearchResponses(searchResponses)}
          staticHeaderInView={staticHeaderInView}
          clickEvent={userTypeAlgoliaClickEvent}
          handleOnSearchResultsClickGA4={dispatchDataLayerEventOnSearchResultsClickGA4}
          messages={searchResultsMessages}
          url={searchResultUrl || ''}
          totalSearchResults={totalSearchResults}
          isOpen={isLiveSearchOpen}
          onClose={closeSearch}
          handleShowAllSearchResultsClickGA={dispatchDataLayerEventOnShowAllSearchResultsClickGA}
          handleNoResultsFoundCTARequestQuote={dispatchDataLayerEventOnNoResultsRequestQuoteGA}
        />
      </div>
    </form>
  );
};

export default Search;
